import React, { useRef } from 'react';
import cx from 'classnames';
import ClusterLayout from '../ClusterLayout';
import ClickArea from '../ClickArea';
import Text from '../Text';
import Tooltip from '../Tooltip';
import Button from '../Button';
import stylesheet from './DataWellGroup.css';
import PositiveArrow, { NegativeArrow } from './icons';

export type DataMetricProps = {
  metric: string;
};

export type DataMetricDescriptorProps = {
  type?: 'positive' | 'negative' | 'link' | 'static';
  href?: string;
  children?: React.ReactNode;
};

export type DataLabelProps = {
  coin?: React.ReactNode;
  tooltip?: string;
  children?: React.ReactNode;
};

export type DataItemProps = {
  children?: React.ReactNode;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  alignment?: 'vertical' | 'horizontal';
  className?: string;
  iconButton?: React.ReactNode;
};

export type DataWellGroupProps = {
  children?: React.ReactNode;
  className?: string;
};

const DataMetric = ({ metric }: DataMetricProps) => {
  return (
    <ClusterLayout className={stylesheet.metric}>
      <p>{metric}</p>
    </ClusterLayout>
  );
};

const DataMetricDescriptor = ({
  type = 'static',
  href,
  children,
}: DataMetricDescriptorProps) => {
  let typeIcon: React.ReactNode;
  if (type && type === 'positive') {
    typeIcon = <PositiveArrow />;
  } else if (type && type === 'negative') {
    typeIcon = <NegativeArrow />;
  }
  return (
    <ClusterLayout gap={1} className={stylesheet.descriptor}>
      {typeIcon && typeIcon}
      {href ? (
        <Button type="plain" size="small" href={href}>
          {children}
        </Button>
      ) : (
        <Text appearance="tiny" className={stylesheet[type]}>
          {children}
        </Text>
      )}
    </ClusterLayout>
  );
};

const DataLabel = ({ coin, tooltip, children }: DataLabelProps) => {
  const dataLabel = (
    <ClusterLayout gap={0} className={stylesheet.dataLabel}>
      {tooltip ? (
        <ClusterLayout gap={coin ? 1 : 0}>
          {coin}
          <Tooltip direction="bottom" label={<Text>{tooltip}</Text>}>
            <div>{children}</div>
          </Tooltip>
        </ClusterLayout>
      ) : (
        <ClusterLayout gap={coin ? 1 : 0}>
          {coin}
          {children}
        </ClusterLayout>
      )}
    </ClusterLayout>
  );

  return dataLabel;
};

const DataItem = ({
  children,
  onClick,
  size = 'medium',
  alignment = 'vertical',
  iconButton,
  className,
}: DataItemProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  return (
    <div
      className={cx([stylesheet.dataItem, stylesheet[size], className], {
        [stylesheet.dataItemInteractive]: onClick,
      })}
    >
      {onClick ? (
        <ClickArea targetRef={targetRef} onClick={onClick}>
          <div ref={targetRef}>
            {children}
            {iconButton ? (
              <div className={stylesheet.iconButtonContainer}>{iconButton}</div>
            ) : null}
          </div>
        </ClickArea>
      ) : (
        <div className={stylesheet[alignment]}>
          {children}
          {iconButton ? (
            <div className={stylesheet.iconButtonContainer}>{iconButton}</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const DataWellGroup = React.forwardRef<HTMLDivElement, DataWellGroupProps>(
  function DataWellGroup({ children, className }, forwardedRef) {
    return (
      <div ref={forwardedRef} className={cx(stylesheet.group, className)}>
        {children}
      </div>
    );
  },
);

export { DataWellGroup, DataItem, DataMetricDescriptor, DataLabel, DataMetric };
